/**
 * What a KIND of part is — the extension point (engine target §9).
 *
 * Nothing outside `kinds/` switches on a kind string. Adding an element is one
 * file in `kinds/` and one line in `kinds/index.ts`; if a part cannot be added
 * that way, this interface is what is missing something.
 */
import { Control, Fact, Part, PartKindKey, Problem, Ray, Surface, ThinElement, Vec2, World } from './types';

export interface Freedom {
    /** It may sit on the rail. */
    onAxis: boolean;
    /** It may turn once free. */
    turns: boolean;
}

/** A point on an object that is imaged: a filament, or a sample along the rod. */
export interface ObjectPoint {
    id: string;
    at: Vec2;
    label: string;
    /** 0 at end A, 1 at end B. Absent for a point source. */
    t?: number;
    /** Rays are drawn from it (as opposed to it only being imaged). */
    traced: boolean;
}

export interface PartKind<D = Record<string, unknown>> {
    key: PartKindKey;
    label: string;
    tag: string;
    create(at: Vec2, id: string): Part<D>;

    /** THE WHOLE OF ITS PHYSICS. */
    surfaces(part: Part<D>, w: World): Surface[];
    /** For a lens or mirror: the textbook's description of it. */
    element?(part: Part<D>, w: World): ThinElement;
    /** Rays it emits on its own: a ray box, a parallel beam. */
    emit?(part: Part<D>, w: World): Emission[];
    /** Points it presents to be imaged. */
    points?(part: Part<D>, w: World): ObjectPoint[];

    /** A closed polygon in bench coordinates — drawn, clicked, selected. */
    outline(part: Part<D>, w: World): Vec2[];
    freedom: Freedom;
    controls(part: Part<D>, w: World): Control[];
    /** Data fields worked out from the others, re-derived after every edit: a
     *  lens set by its radii keeps its `f` in step with them. */
    derive?(part: Part<D>, w: World): Partial<D>;
    keyFacts?(part: Part<D>, w: World): Fact[];
    name(part: Part<D>, w: World): string;
    diagnose?(part: Part<D>, w: World): Problem[];
}

/** One ray a source emits: where from, which way, and what it is. */
export interface Emission {
    from: Vec2;
    direction: Vec2;
    point: string;
    role: Ray['role'];
}

const REGISTRY = new Map<PartKindKey, PartKind<never>>();

export function registerKind<D>(kind: PartKind<D>): void {
    REGISTRY.set(kind.key, kind as unknown as PartKind<never>);
}

export function kindOf(key: PartKindKey): PartKind<never> {
    const k = REGISTRY.get(key);
    if (!k) throw new Error(`optics: no part kind "${key}" is registered`);
    return k;
}

export const allKinds = (): PartKind<never>[] => [...REGISTRY.values()];
export const defOf = (part: Part): PartKind<Record<string, unknown>> =>
    kindOf(part.kind) as unknown as PartKind<Record<string, unknown>>;

const SUB = '₀₁₂₃₄₅₆₇₈₉';
const subscript = (n: number): string => String(n).split('').map(d => SUB[Number(d)]).join('');

/** L₁, M₂ … numbered in the order the parts were placed. An authored label wins. */
export function numbered(part: Part, w: World, tag: string): string {
    if (part.label) return part.label;
    const same = w.parts.filter(p => defOf(p).tag === tag);
    const i = same.findIndex(p => p.id === part.id);
    return `${tag}${subscript(i < 0 ? same.length + 1 : i + 1)}`;
}
